import { useMemo, useState } from "react";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import PageHeader from "@/components/base/PageHeader";
import NewsCard from "@/components/base/NewsCard";
import type { NewsItem } from "@/types";
import { news as rawNews } from "@/mocks/news";

const timeOptions = [
  { label: "全部时间", value: "all" },
  { label: "最近一月", value: "1" },
  { label: "最近半年", value: "6" },
  { label: "最近一年", value: "12" },
];

function withinMonths(dateStr: string, months: number) {
  const date = new Date(dateStr).getTime();
  const now = new Date();
  const limit = new Date();
  limit.setMonth(now.getMonth() - months);
  return date >= limit.getTime();
}

export default function News() {
  useDocumentMeta({
    title: "舒淇最新动态 - 新闻列表",
    description:
      "舒淇最新动态新闻列表，汇集舒淇的电影项目、活动现场、媒体专访等最新资讯，支持按时间筛选与关键词搜索，随时掌握舒淇的最新消息。",
    keywords: "舒淇动态,舒淇新闻,舒淇最新消息,舒淇资讯",
  });

  const news = (rawNews as unknown as NewsItem[])
    .slice()
    .sort((a, b) => (a.date < b.date ? 1 : -1));

  const [query, setQuery] = useState("");
  const [time, setTime] = useState("all");
  const [visible, setVisible] = useState(4);

  const filtered = useMemo(() => {
    const keyword = query.trim().toLowerCase();
    return news.filter((item) => {
      if (time !== "all" && !withinMonths(item.date, Number(time))) return false;
      if (keyword) {
        const haystack = `${item.title} ${item.summary} ${item.category}`.toLowerCase();
        if (!haystack.includes(keyword)) return false;
      }
      return true;
    });
  }, [news, query, time]);

  const shown = filtered.slice(0, visible);
  const hasMore = visible < filtered.length;

  const resetAll = () => {
    setQuery("");
    setTime("all");
    setVisible(4);
  };

  return (
    <div>
      <PageHeader
        eyebrow="Latest News"
        title="舒淇最新动态"
        description="第一时间为你整理舒淇的影视项目、出席活动、专访报道等最新资讯，随时掌握她的最新动态。"
      />

      <section className="max-w-content mx-auto px-4 md:px-8 py-10 md:py-14">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex flex-wrap gap-2">
            {timeOptions.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => {
                  setTime(opt.value);
                  setVisible(4);
                }}
                className={`px-4 py-2 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${
                  time === opt.value
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-100 text-foreground-700 hover:bg-background-200"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>

          <div className="relative w-full lg:w-80">
            <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-secondary-500" />
            <input
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setVisible(4);
              }}
              placeholder="搜索资讯标题、关键词…"
              className="w-full pl-11 pr-4 py-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-950 placeholder:text-secondary-400 focus:outline-none focus:border-accent-400 focus:ring-2 focus:ring-accent-100"
            />
          </div>
        </div>

        <p className="text-sm text-secondary-500 mt-6">
          共 <strong className="text-foreground-950">{filtered.length}</strong> 条资讯
        </p>

        {shown.length > 0 ? (
          <>
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
              {shown.map((item) => (
                <NewsCard key={item.id} item={item} query={query} />
              ))}
            </div>

            {hasMore && (
              <div className="mt-10 text-center">
                <button
                  type="button"
                  onClick={() => setVisible((v) => v + 4)}
                  className="inline-flex items-center gap-2 px-7 py-3 rounded-md border border-background-300 text-sm font-medium text-foreground-700 hover:border-accent-400 hover:text-accent-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-add-line" />
                  加载更多
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="mt-6 rounded-lg border border-dashed border-background-300 bg-background-50 py-20 text-center">
            <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-secondary-400">
              <i className="ri-newspaper-line text-3xl" />
            </div>
            <p className="mt-5 font-heading text-lg text-foreground-950">
              暂无相关资讯
            </p>
            <p className="mt-2 text-sm text-foreground-600">
              换个关键词或时间范围试试看
            </p>
            <button
              type="button"
              onClick={resetAll}
              className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-accent-500 text-background-50 text-sm hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-refresh-line" />
              重置筛选条件
            </button>
          </div>
        )}
      </section>
    </div>
  );
}